/* eslint-disable max-lines */
import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { CounterNumberInput, DialogStep, FCWithFragments } from '@modernloop/shared/components';
import { assertIsoTimestamp } from '@modernloop/shared/datetime';
import { useFlag } from '@modernloop/shared/feature-flag';
import { useIsMobile } from '@modernloop/shared/hooks';
import { CaretLeftIcon, InfoIcon, SparkleIcon } from '@modernloop/shared/icons';
import { Alert, AlertTitle, Box, Container, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { addDays, startOfDay as startOfDayDateFns } from 'date-fns';
import { noop } from 'lodash';

import {
  BaseCandidateAvailabilityOption_InterviewPlanFragment,
  CandidateAvailabilityOption_InterviewPlanFragment,
  SuggestedTimeRangesRequestType,
  useTaskInterviewPlanQuery,
} from 'src/generated/mloop-graphql';

import useEqualSizeSxProps from 'src/components/utils/useEqualSizeSxProps';

import useInterviewPlanEntityHasInterviewHiddenFromCandidate from 'src/entities/InterviewPlan/useInterviewPlanHasInterviewHiddenFromCandidate';
import useIsInterviewPlanEntityValid from 'src/entities/InterviewPlan/useIsInterviewPlanValid';
import {
  DEFAULT_TIMEFRAME_DAYS,
  MAX_TIMEFRAME_DAYS,
  MIN_TIMEFRAME_DAYS,
  parseTimeframeDays,
} from 'src/entities/SchedulingRequestSettings/Timeframe/TimeframeDays';

import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';
import useUserId from 'src/hooks/useUserId';

import {
  CandidateAvailabilityOptions,
  DEFAULT_MINUTES_PER_DAY,
  DEFAULT_NUMBER_OF_DAYS,
} from 'src/store/slices/candidate-availability-options';

import IsoTimestamp from 'src/types/IsoTimestamp';

import OptionsCountAlert from 'src/views-new/CandidateRequest/OptionsCountAlert';
import { TaskCreateInputWrapper } from 'src/views-new/ScheduleTask/CreateModals/shared/types';
import OptionsCalendar from 'src/views-new/SelfSchedule/OptionsCalendar';
import TimeFrame, { NumberOfDays } from 'src/views-new/SelfSchedule/TimeframePicker';

import { AdvanceNoticeHours, CandidateAvailabilityOptionsPreferences } from './CandidateAvailabilityOptionsPreferences';
import { DayCount } from './DayCountPicker';

type Fragments = {
  interviewPlan: BaseCandidateAvailabilityOption_InterviewPlanFragment;
};

type Props = {
  applicationId: string;
  taskId?: string;
  taskInput?: TaskCreateInputWrapper;
  timezone: string;
  options: CandidateAvailabilityOptions;
  onOptionsChanged: (options: CandidateAvailabilityOptions) => void;
  onBack?: () => void;
  onClose: () => void;
  onSave: (config: CandidateAvailabilityOptions) => Promise<void>;
};

export function useRoundedMinsPerDay(options: CandidateAvailabilityOptions) {
  return useMemo(() => {
    if (!options) {
      return DEFAULT_MINUTES_PER_DAY;
    }

    return options.minutesPerDays >= options.minimumTimeBlockMinutes
      ? options.minutesPerDays
      : options.minimumTimeBlockMinutes;
  }, [options]);
}

const BaseCandidateAvailabilityOption: FCWithFragments<Fragments, Props> = ({
  applicationId,
  taskId,
  taskInput,
  timezone,
  interviewPlan,
  options: optionsProp,
  onOptionsChanged,
  onBack,
  onClose,
  onSave,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const equalSizeSxProps = useEqualSizeSxProps();
  const isFullScreen = useIsMobile();
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');
  const daysInputEnabled = useFlag('org_self_schedule_timeframe_in_days');

  const { data: taskInterviewPlan } = useTaskInterviewPlanQuery({
    variables: { id: taskId },
    skip: !taskId,
  });

  const [saving, setSaving] = useState(false);
  const [haveTogglesChanged, setHaveTogglesChanged] = useState(false);
  // The day count the counter shows. Hand-picked dates leave it on the last value, because the
  // counter has no empty state and the picked days are not a window that starts today.
  const [timeframeDays, setTimeframeDays] = useState(optionsProp.timeframeNumberOfDays || DEFAULT_TIMEFRAME_DAYS);

  const interviewPlanId = taskInput?.customJobStageId || taskInterviewPlan?.task?.interviewPlan?.id;

  const {
    isValid: isValidInterviewPlan,
    title: interviewPlanTitle,
    error: interviewPlanError,
  } = useIsInterviewPlanEntityValid(
    {
      jobStage: interviewPlan || { id: interviewPlanId, jobStageInterviewGroups: [] },
    },
    { isDayBreakAllowed: false, allowAllInterviewsHiddenFromCandiate: true, allowDynamicBreaks: false }
  );

  const hasInterviewHiddenFromCandidate = useInterviewPlanEntityHasInterviewHiddenFromCandidate({
    interviewPlan,
  });

  const options = useMemo(() => {
    // When there are interviews hidden from candidate we want to disable the suggested availability toggles.
    return {
      ...optionsProp,
      canScheduleOverAvailableKeywords: hasInterviewHiddenFromCandidate
        ? false
        : optionsProp.canScheduleOverAvailableKeywords,
      canScheduleOverRecruitingKeywords: hasInterviewHiddenFromCandidate
        ? false
        : optionsProp.canScheduleOverRecruitingKeywords,
      canScheduleOverFreeTime: hasInterviewHiddenFromCandidate ? false : optionsProp.canScheduleOverFreeTime,
      shouldRespectLoadLimit: hasInterviewHiddenFromCandidate ? false : optionsProp.shouldRespectLoadLimit,
    };
  }, [hasInterviewHiddenFromCandidate, optionsProp]);

  const roundedMinsPerDay = useRoundedMinsPerDay(options);

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedDatesOptionsCount, setSelectedDatesOptionsCount] = useState(0);
  const [selfScheduleCountError, setSelfScheduleCountError] = useState<string>();

  const getInclusionDays = useCallback((value: number) => {
    let date = startOfDayDateFns(new Date());
    const inclusionDays: IsoTimestamp[] = [];

    for (let i = 0; i < value; i++) {
      inclusionDays.push(assertIsoTimestamp(date.toISOString()));
      date = addDays(date, 1);
    }

    return inclusionDays;
  }, []);

  const handleSelectDays = (value: DayCount) => {
    onOptionsChanged({ ...options, numberOfDays: value });
  };

  const handleSelectMinsPerDay = (mins: number) => {
    onOptionsChanged({
      ...options,
      minutesPerDays: mins,
      minimumTimeBlockMinutes: options.minimumTimeBlockMinutes > mins ? mins : options.minimumTimeBlockMinutes,
    });
  };

  const handleSelectMinTimeBlock = (mins: number) => {
    onOptionsChanged({ ...options, minimumTimeBlockMinutes: mins });
  };

  const handleSelectAdvanceNoticeHours = (value: AdvanceNoticeHours) => {
    onOptionsChanged({ ...options, advanceNoticeMinutes: value * 60 });
  };

  const handleNotesBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onOptionsChanged({ ...options, candidateNote: event.target.value });
  };

  const handleSaveAndContinueClick = () => {
    setSaving(true);
    const finalOptions = { ...options };
    finalOptions.minutesPerDays = roundedMinsPerDay;
    finalOptions.isValidInterviewPlan = isValidInterviewPlan;

    // eslint-disable-next-line promise/catch-or-return
    onSave(finalOptions).finally(() => {
      setSaving(false);
    });
  };

  const handleTimeframeDaysChange = (value: number) => {
    if (options.useRollingDays) {
      onOptionsChanged({ ...options, timeframeNumberOfDays: value, inclusionDays: undefined });
    } else {
      onOptionsChanged({ ...options, timeframeNumberOfDays: value, inclusionDays: getInclusionDays(value) });
    }
  };

  /** Shows and forwards only a day count the parser accepts, so the counter never shows a bad one. */
  const handleTimeframeDaysCounterChange = (days: number) => {
    parseTimeframeDays(days).map((parsedDays) => {
      setTimeframeDays(parsedDays);
      handleTimeframeDaysChange(parsedDays);
    });
  };

  const handleSelectedDatesChanged = useCallback(
    (dates: IsoTimestamp[]) => {
      onOptionsChanged({
        ...options,
        inclusionDays: dates,
        useRollingDays: false,
      });
    },
    [onOptionsChanged, options]
  );

  const inclusionDays: IsoTimestamp[] | undefined = useMemo(() => {
    if (!options) {
      return undefined;
    }

    if (options.useRollingDays) {
      return undefined;
    }

    if (options.inclusionDays) {
      return options.inclusionDays as IsoTimestamp[];
    }

    return [];
  }, [options]);

  if (!options) {
    return null;
  }

  const suggestedOptionsOn =
    options.canScheduleOverAvailableKeywords ||
    options.canScheduleOverRecruitingKeywords ||
    options.canScheduleOverFreeTime;

  return (
    <DialogStep
      title={t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.title')}
      subTitle={
        !onBack
          ? t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.stepWithoutBack')
          : t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.stepWithBack')
      }
      submitOptions={{
        dataTestId: 'candidate-availability-options-continue-button',
        label: t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.continue'),
        isDisabled: Boolean(saving) || (!options.useRollingDays && !inclusionDays?.length),
        tooltip:
          !options.useRollingDays && !inclusionDays?.length
            ? t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.noDatesSelected')
            : undefined,
        isLoading: saving,
        onClick: handleSaveAndContinueClick,
      }}
      cancelOptions={
        !onBack
          ? {
              label: t('hiring-dashboard-common:common.cancel'),
              onClick: () => onClose(),
            }
          : {
              label: t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.previousStep'),
              onClick: onBack,
              startIcon: <CaretLeftIcon />,
            }
      }
      onClose={onClose}
    >
      {((taskId && taskInterviewPlan) || !!taskInput) && (
        <Stack direction="column" spacing={1} useFlexGap mb={1}>
          <>
            <Typography fontWeight={600}>
              {t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.whatDaysCandidate')}
            </Typography>
            <Stack
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              spacing={1}
              sx={equalSizeSxProps.equalSizeChild}
            >
              <Typography>
                {daysInputEnabled
                  ? t('hiring-dashboard-schedule:schedule.requestSettings.timeframeDaysLabel')
                  : t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.timeframe')}
              </Typography>
              {daysInputEnabled ? (
                // The equal-size rule gives the control half of the row, so keep the counter at the
                // right edge, in line with the switch below it.
                <Box
                  data-testid="scheduling-request-timeframe-days-counter"
                  sx={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <CounterNumberInput
                    value={timeframeDays}
                    min={MIN_TIMEFRAME_DAYS}
                    max={MAX_TIMEFRAME_DAYS}
                    onChange={handleTimeframeDaysCounterChange}
                  />
                </Box>
              ) : (
                <TimeFrame
                  timeFrame={options.timeframeNumberOfDays || NumberOfDays.TwoWeeks}
                  onChange={handleTimeframeDaysChange}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} ml={-1.5}>
              <FormControlLabel
                sx={{
                  width: '100%',
                  justifyContent: 'space-between',
                }}
                labelPlacement="start"
                label={t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.useRollingWindow')}
                control={
                  <Switch
                    checked={options.useRollingDays}
                    onChange={(event) => {
                      if (!event.target.checked) {
                        onOptionsChanged({
                          ...options,
                          useRollingDays: false,
                          inclusionDays: options.timeframeNumberOfDays
                            ? getInclusionDays(options.timeframeNumberOfDays)
                            : options.inclusionDays,
                        });
                      } else {
                        onOptionsChanged({
                          ...options,
                          useRollingDays: true,
                          timeframeNumberOfDays: options.timeframeNumberOfDays || NumberOfDays.TwoWeeks,
                          inclusionDays: undefined,
                        });
                      }
                    }}
                  />
                }
              />
              <InfoIcon
                tooltip={t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.rollingWindowTooltip')}
              />{' '}
            </Stack>
          </>
          <Container
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Paper
              elevation={0}
              variant={isFullScreen ? undefined : 'outlined'}
              sx={{
                width: '380px',
                pl: '10px',
              }}
            >
              <OptionsCalendar
                applicationId={applicationId}
                taskId={taskId}
                requestType={SuggestedTimeRangesRequestType.AvailabilityRequest}
                jobStageId={options.jobStageId}
                customJobStageId={taskInterviewPlan?.task?.interviewPlan?.id || taskInput?.customJobStageId}
                customInterviewPlan={
                  orgBreakImprovementsEnabled
                    ? taskInput?.interviewPlan?.groups || undefined
                    : taskInput?.customInterviewPlan || undefined
                }
                rollingDays={
                  options?.useRollingDays ? options.timeframeNumberOfDays || NumberOfDays.TwoWeeks : undefined
                }
                inclusionDays={inclusionDays}
                advanceNoticeInHours={options.advanceNoticeMinutes ? options.advanceNoticeMinutes / 60 : 0}
                onLoading={setLoadingOptions}
                onSelectedDatesOptionsCountChanged={setSelectedDatesOptionsCount}
                onSelectedDatesChanged={handleSelectedDatesChanged}
                onEmployeeMissingZoomUserId={noop}
                onSelfScheduleCountError={setSelfScheduleCountError}
                shouldRespectLoadLimit={options.shouldRespectLoadLimit ?? true}
                canScheduleOverAvailableKeywords={options.canScheduleOverAvailableKeywords ?? false}
                canScheduleOverRecruitingKeywords={options.canScheduleOverRecruitingKeywords ?? false}
                canScheduleOverFreeTime={options.canScheduleOverFreeTime ?? false}
                skipFetchingOptions={
                  (!suggestedOptionsOn && !haveTogglesChanged) ||
                  hasInterviewHiddenFromCandidate ||
                  !isValidInterviewPlan ||
                  !interviewPlan.id // This means interview plan is not yet available
                }
              />
            </Paper>
          </Container>
          {!isValidInterviewPlan && (
            <Alert severity="info" icon={<SparkleIcon color="warning" />}>
              <AlertTitle>
                {t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.suggestTimesTitle')}
              </AlertTitle>
              {interviewPlanTitle !== 'Multi-day schedule found' && interviewPlanError}
              {interviewPlanTitle === 'Multi-day schedule found' &&
                t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.multiDayScheduleMessage')}
            </Alert>
          )}
        </Stack>
      )}

      {hasInterviewHiddenFromCandidate && (
        <Alert severity="info">
          <AlertTitle>
            {t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.suggestedTimesNotAvailable')}
          </AlertTitle>
          {t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.suggestedTimesHiddenEvents')}
        </Alert>
      )}

      <CandidateAvailabilityOptionsPreferences
        key={options.jobStageId}
        options={options}
        disableToggles={!isValidInterviewPlan || hasInterviewHiddenFromCandidate}
        candidateTimezone={timezone}
        roundedMinsPerDay={roundedMinsPerDay}
        onNumberOfDaysChange={handleSelectDays}
        onMinutesPerDayChange={handleSelectMinsPerDay}
        onMinTimeBlockMinutesChange={handleSelectMinTimeBlock}
        onAdvanceNoticeHoursChange={handleSelectAdvanceNoticeHours}
        onCandidateNoteBlur={handleNotesBlur}
        onScheduleOverAvailableKeywordsChange={(canScheduleOverAvailableKeywords) => {
          setHaveTogglesChanged(true);
          onOptionsChanged({ ...options, canScheduleOverAvailableKeywords });
        }}
        onScheduleOverRecruitingKeywordsChange={(canScheduleOverRecruitingKeywords) => {
          setHaveTogglesChanged(true);
          onOptionsChanged({ ...options, canScheduleOverRecruitingKeywords });
        }}
        onScheduleOverFreeTimeChange={(canScheduleOverFreeTime) => {
          setHaveTogglesChanged(true);
          onOptionsChanged({ ...options, canScheduleOverFreeTime });
        }}
        onRespectLoadLimitChange={(shouldRespectLoadLimit) => {
          onOptionsChanged({ ...options, shouldRespectLoadLimit });
        }}
        onBookIfPerfectScheduleFoundChange={(bookIfPerfectScheduleFound) => {
          onOptionsChanged({ ...options, bookIfPerfectScheduleFound });
        }}
        alertJsx={
          isValidInterviewPlan && (
            <OptionsCountAlert
              enabled={suggestedOptionsOn ?? false}
              loading={loadingOptions}
              count={selectedDatesOptionsCount}
              error={suggestedOptionsOn ? selfScheduleCountError : undefined}
            />
          )
        }
      />
    </DialogStep>
  );
};

BaseCandidateAvailabilityOption.fragments = {
  interviewPlan: gql`
    ${useIsInterviewPlanEntityValid.fragments.jobStage}
    ${useInterviewPlanEntityHasInterviewHiddenFromCandidate.fragments.interviewPlan}
    fragment BaseCandidateAvailabilityOption_interviewPlan on JobStage {
      id
      ...useIsInterviewPlanValid_jobStage
      ...useInterviewPlanHasInterviewHiddenFromCandidate_interviewPlan
    }
  `,
};

type CandidateAvailabilityOptionFragments = {
  interviewPlan: CandidateAvailabilityOption_InterviewPlanFragment;
};

type CandidateAvailabilityOptionProps = {
  applicationId: string;
  taskId?: string;
  taskInput?: TaskCreateInputWrapper;
  timezone: string;
  options: CandidateAvailabilityOptions;
  onOptionsChanged: (options: CandidateAvailabilityOptions) => void;
  onContinue: () => void;
  onClose: () => void;
  onBack?: () => void;
};

const CandidateAvailabilityOption: FCWithFragments<
  // eslint-disable-next-line modernloop/restric-fragments-name.cjs
  CandidateAvailabilityOptionFragments,
  // eslint-disable-next-line modernloop/restric-fragments-name.cjs
  CandidateAvailabilityOptionProps
> = ({
  applicationId,
  timezone,
  options,
  taskId,
  taskInput,
  interviewPlan,
  onOptionsChanged,
  onBack,
  onContinue,
  onClose,
}): JSX.Element => {
  const { logEvent } = useLogEvent();
  const userId = useUserId();

  const handleSaveAndContinue = async (newOptions: CandidateAvailabilityOptions) => {
    const finalOptions: CandidateAvailabilityOptions = { ...newOptions };

    onOptionsChanged(newOptions);

    logEvent(LoggerEvent.CLIENT_CANDIDATE_AVAILABILITY_OPTIONS, {
      isDefaultNumberOfDays: finalOptions.numberOfDays !== DEFAULT_NUMBER_OF_DAYS,
      userId,
    });

    onContinue();
  };

  return (
    <BaseCandidateAvailabilityOption
      applicationId={applicationId}
      timezone={timezone}
      options={options}
      taskInput={taskInput}
      taskId={taskId}
      interviewPlan={interviewPlan}
      onOptionsChanged={onOptionsChanged}
      onClose={onClose}
      onBack={onBack}
      onSave={handleSaveAndContinue}
    />
  );
};

CandidateAvailabilityOption.fragments = {
  interviewPlan: gql`
    ${BaseCandidateAvailabilityOption.fragments.interviewPlan}
    fragment CandidateAvailabilityOption_interviewPlan on JobStage {
      id
      ...BaseCandidateAvailabilityOption_interviewPlan
    }
  `,
};

export default CandidateAvailabilityOption;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQL = gql`
  query TaskInterviewPlan($id: uuid!) {
    task(id: $id) {
      interviewPlan {
        id
      }
    }
  }
`;
